import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { LanguagesList } from './languages';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { ApiService } from '@core/api.service';
import { PlanPermissions, RolePermissions } from '@core/role-permissions';
import { IUserRegistration } from '@features/auth/interfaces/user-registration.interface';
import { RegisterParams } from '@features/auth/register-params';
import { UserRoles } from '@features/auth/user-roles';
import { MarketplaceService } from '@features/marketplace/marketplace.service';
import { TranslateService } from '@ngx-translate/core';
import { IUserData } from '@sadmin/shared/interfaces/user.interface';
import { PQLEventService } from '@services/common/pql-event-tracking.service';
import { SegmentService } from '@services/common/segment.service';
import { StorageService } from '@services/common/storage.service';
import { TimeService } from '@services/common/time.service';
import { PlansEnum } from '@shared/components/select-plan/select-plan/plan-mapper.service';
import { TrackEventName } from '@shared/enums/track-event.enum';
import { ICurrency } from '@shared/interfaces/entity/currency.interface';
import { ILanguage } from '@shared/interfaces/entity/language.interface';
import { ISubscription } from '@shared/interfaces/entity/subscription.interface';
import { IUser } from '@shared/interfaces/entity/user.interface';
import { ToastStatusEnum } from '@ui-kit/app-toast/toast-status.enum';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { CrudService } from 'collection';
import { Item, ItemModel, PostEntityModel } from 'collection';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _user$: BehaviorSubject<IUser> = new BehaviorSubject(null);
  private _subscription$: BehaviorSubject<ISubscription> = new BehaviorSubject(null);

  user: IUser;
  subscription: ISubscription;
  currency: ICurrency;
  token: string;
  profileItem: ItemModel;
  loginRequest: PostEntityModel;
  registerRequest: PostEntityModel;
  resetPasswordRequest: PostEntityModel;
  sentRecoverRequest: PostEntityModel;
  userRegistrationRequest: PostEntityModel;

  userLogin$: Subject<boolean> = new Subject();
  userLanguage$: BehaviorSubject<ILanguage> = new BehaviorSubject(null);
  skipSuperAdminLogin = true;

  rolePermissions = {
    ...RolePermissions
  };

  $user = this._user$.asObservable();

  $subscription = this._subscription$.asObservable();

  constructor(
    private _crud: CrudService,
    private _api: ApiService,
    private _translateService: TranslateService,
    private _localStorage: StorageService,
    private _timeService: TimeService,
    private _toastService: ToasterService,
    private _marketplaceService: MarketplaceService,
    private _segmentService: SegmentService,
    private _PqlEventTrackerService: PQLEventService
  ) {
    this.profileItem = new Item({
      api: this._api.userMyProfile,
      params: {
        expand: `role,
        status,
        country,
        language,
        hide_scheduler,
        email_confirmed,
        account_created_at,
        onboarding,
        getting_started,
        hubspot_token,
        subscription.currency,
        account,
        account.country,
        currency,
        subscription.plan,
        moduleRequest,
        user_module_mark`
      }
    });

    this.loginRequest = this._api.login;
    this.registerRequest = this._api.register;
    this.userRegistrationRequest = this._api.userRegistration;
    this.resetPasswordRequest = this._api.resetPassword;
    this.sentRecoverRequest = this._crud.createPostEntity({ name: 'password-reset-request' });

    this.initUser();
  }

  getLocalTimezone(): number {
    return this._timeService.getLocalTimezone();
  }

  getUserTimezone(): string {
    return this.user.timezone.offset_full_name;
  }

  showMessageForStaff(): void {
    this._toastService.show({
      type: ToastStatusEnum.Warning,
      message: marker('Please login to SAdmin system!')
    });
  }

  initSegmentOnLogin(response, type): void {
    const updatedUser = this._segmentService.createUpdatedUser({ data: response });

    this._segmentService.identify(updatedUser);

    if (response.user.role !== 'super_admin' && response.user.role !== 'staff') {
      this._segmentService.group(response.subscription);
    }

    this._segmentService.track('User Login', {
      login: true,
      login_type: type,
      first_name: response.user.first_name,
      last_name: response.user.last_name
    });
  }

  initSegmentOnLogout(): void {
    const user = this.user;

    this._segmentService.track('User Logout', {
      logout: true,
      email: user ? user.email : null
    });
  }

  login(params: any) {
    if (this.loginRequest.loading$.value) {
      return false;
    }

    return this.handleLoginRequest(params, this.loginRequest, 'email');
  }

  handleLoginRequest(data, request, type) {
    return request.save({ data: data }).pipe(
      filter((response: { data: any }) => {
        if (!this.skipSuperAdminLogin) {
          return true;
        }
        const lResponse = response.data;

        const isStaffOrAdmin = lResponse.user.role === UserRoles.SuperAdmin || lResponse.user.role === UserRoles.Staff;

        if (isStaffOrAdmin) {
          this.showMessageForStaff();
        }
        return !isStaffOrAdmin;
      }),
      map((response: any) => {
        const lResponse = response.data;
        this.currency = lResponse.currency;
        if (!this.skipSuperAdminLogin) {
          this.setUser(lResponse.user);
        }
        this.setSubscription(lResponse.subscription);
        this.setLocalStorage(lResponse);

        if (this.skipSuperAdminLogin) {
          this.initSegmentOnLogin(lResponse, type);
        }

        this.userLogin$.next(true);

        const isStaffOrAdmin = lResponse.user.role === UserRoles.SuperAdmin || lResponse.user.role === UserRoles.Staff;
        if (!isStaffOrAdmin) {
          this._PqlEventTrackerService.track(TrackEventName.LOGIN);
        }
        return response.data;
      }),
      switchMap((res) => {
        if (!this.skipSuperAdminLogin) {
          return of(res);
        }

        return this.profileItem.load().pipe(
          map(() => {
            this.setUser(this.profileItem.data);
            return res;
          })
        );
      }),
      switchMap((res) => {
        if (!this.skipSuperAdminLogin) {
          return of(res);
        } else {
          return this._marketplaceService.collectionIntegration.reload().pipe(
            map((items) => {
              this._marketplaceService.setIntegrations(items);
              return res;
            })
          );
        }
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  setLocalStorage(response): void {
    const timezone = this.getLocalTimezone();

    this._localStorage
      .setItem('token', response['access_token'])
      .setItem('user', response?.user)
      .setItem('avatar', response?.avatar)
      .setItem('language', response?.language)
      .setItem('timezone', timezone)
      .setItem('currency', response?.currency)
      .setItem('subscription', response?.subscription)
      .setItem('onboarding', response?.onboarding)
      .setItem('accountInf', response['account_licenses'] ? response['account_licenses'].items[0] : null)
      .setItem('user-hash', response?.intercom_user_hash)
      .setItem('userId', response?.user?.id);
  }

  sentRecoverLink(email: string): Observable<object> {
    return this.sentRecoverRequest.save({ data: { email, version: 'new' } });
  }

  resetPassword(password: string, token: string): Observable<object> {
    return this.resetPasswordRequest.save({ data: { password, token } });
  }

  registerUser(params: IUserRegistration, token: string): Observable<object> {
    if (this.userRegistrationRequest.loading$.value) {
      return;
    }

    return this.userRegistrationRequest.save({ data: params, params: { token: token } }).pipe(
      map((response: any) => {
        const lResponse = response.data;

        this.setUser(lResponse.user);
        this.setSubscription(lResponse.subscription);
        this.currency = lResponse.currency;
        this._segmentService.identify(lResponse.user);
        this.setLocalStorage(lResponse);
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  register(params: RegisterParams): Observable<object> {
    if (this.registerRequest.loading$.value) {
      return;
    }

    return this.registerRequest.save({ data: params }).pipe(
      map((response: any) => {
        const lResponse = response.data;

        this.setUser(lResponse.user);
        this.setSubscription(lResponse.subscription);
        this.currency = lResponse.currency;
        this.setLocalStorage(lResponse);

        return response;
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  logout(): void {
    if (this.user?.role !== 'super_admin' && this.user?.role !== 'staff') {
      try {
        this._segmentService.reset();
        this.initSegmentOnLogout();
      } catch (e) {
        /* empty */
      }
    }

    this._localStorage.clearStorage();
    window.location.reload();
  }

  get currentUser(): IUser {
    return this.user;
  }

  get currentRole(): UserRoles {
    return this.user && this.user.role;
  }

  get currentSubscription(): ISubscription {
    return this.subscription;
  }

  get currentCurrency(): ICurrency {
    return this.currency;
  }

  get accountCurrency(): string {
    return this.user?.currency?.symbol || '$';
  }

  get accountCountryCode(): string {
    return this.user?.account?.country?.two_letter_code;
  }

  getId(): number {
    return this.user && this.user.id;
  }

  isAdmin(): boolean {
    return this.user && this.user.role === UserRoles.Admin;
  }

  isManager(): boolean {
    return this.user && this.user.role === UserRoles.Manager;
  }

  isWorker(): boolean {
    return this.user && this.user.role === UserRoles.Worker;
  }

  isPlanLead(plan?): boolean {
    return !plan
      ? !!(this.subscription && this.subscription.plan && this.subscription.plan.name === PlansEnum.Free)
      : !!(plan && plan?.name === PlansEnum.Free);
  }

  isPlanTrial(): boolean {
    return !!(this.subscription && this.subscription.plan && this.subscription.plan.name === PlansEnum.Trial);
  }

  isPlanSmart(): boolean {
    return !!(this.subscription && this.subscription.plan && this.subscription.plan.name === PlansEnum.Basis);
  }

  isPlanRegular(): boolean {
    return !!(this.subscription && this.subscription.plan && this.subscription.plan.name === PlansEnum.Regular);
  }

  getAccessToken(): string {
    return this._localStorage.getItem('token');
  }

  can(permissionKey: string): boolean {
    if (!permissionKey || this.user?.role === undefined) {
      return false;
    }
    const role = this.user.role;
    return this.rolePermissions[role].indexOf(permissionKey) !== -1;
  }

  setSubscription(subscriptionData): void {
    this.subscription = subscriptionData;
    this._subscription$.next(subscriptionData);
    this._localStorage.setItem('subscription', subscriptionData);

    if (this.user && this.user?.role !== 'super_admin' && this.user?.role !== 'staff') {
      this.mergePermissions();
    }
  }

  setUser(userData): void {
    let user;
    if (!userData?.id) {
      const userFromLocalStorage = this._localStorage.getItem('user');
      user = {
        ...userData,
        id: userFromLocalStorage?.id
      };
    } else {
      user = userData;
    }

    this.user = user;
    this._localStorage.setItem('user', user);
    this.setUserLanguage(user.language);
    this._user$.next(user);
  }

  initProfile(): Observable<IUser | null> {
    this.profileItem.load().subscribe(
      () => {
        this.setUser(this.profileItem.data);
      },
      () => {
        return of(null);
      }
    );

    return this.$user.pipe(filter((user) => user !== null));
  }

  clear(): void {
    this.user = null;
    this.token = null;
    this.profileItem.clear();
    this._user$.next(this.user);
  }

  updateProfileData(data): void {
    const updatedData = {
      ...this.profileItem.data,
      ...data
    };
    this.profileItem.setProperty('data', updatedData);
  }

  setUserLanguage(language: ILanguage): void {
    this.userLanguage$.next(language);
  }

  initUserLanguage(): void {
    const authUser = this.currentUser;

    if (authUser) {
      this._translateService.use(LanguagesList.get(authUser.language_id));
    } else {
      if (!this._translateService.currentLang) {
        const browserLanguage = this._translateService.getBrowserLang();
        const supportedLanguages = ['da', 'de', 'en', 'lt', 'lv', 'nb', 'pl', 'ru', 'sr'];
        const language = supportedLanguages.includes(browserLanguage) ? browserLanguage : 'nb';
        this._translateService.use(language);
      }
    }

    if (authUser && !this.profileItem.loaded && !this.profileItem.loading) {
      if (authUser.role !== 'super_admin' && authUser.role !== 'staff') {
        this.initProfile();
      }
    }
  }

  private initUser(): void {
    const user = this._localStorage.getItem('user');
    const token = this._localStorage.getItem('token');
    const subscription = this._localStorage.getItem('subscription');
    this.currency = this._localStorage.getItem('currency') || null;

    if (user && token) {
      this.token = token;
      this.setUserLanguage(user.language);
      this.setUser(user);
    }

    if (subscription) {
      this.setSubscription(subscription);
    }
  }

  private mergePermissions(): void {
    try {
      const planName = this.subscription.plan.display_name;

      const planPermissions = PlanPermissions[planName];

      if (planPermissions) {
        this.rolePermissions = {
          admin: [...RolePermissions.admin, ...planPermissions.admin],
          manager: [...RolePermissions.manager, ...planPermissions.manager],
          worker: [...RolePermissions.worker, ...planPermissions.worker]
        };
      } else {
        this.rolePermissions = RolePermissions;
      }
    } catch (e) {
      /* empty */
    }
  }
}
